import type { RoomId, Tier } from './ids';

/**
 * The ONLY place for balance numbers (docs/design/economy-progression.md §12, content-schemas §8.2).
 * Components and systems read these; never hard-code a price, threshold or timer elsewhere.
 */
export interface CozinessWeights {
  furniture: number;
  decor: number;
  fixture: number;
  product: number;
  productCap: number;
  rightSpot: number;
  themeDiversity: number;
  themeDiversityCap: number;
  setBonus: number;
  maxCountedCopiesPerItem: number;
}

export interface Balance {
  startCoins: number;
  customerIntervalSec: number;
  customerIntervalJitterSec: number;
  customerIntervalStepPerCozyLevelSec: number;
  customerIntervalMinSec: number;
  /** Seconds the `faster_customers` perk (Tiệm Coziness Lv1) takes off the customer interval. */
  fasterCustomersCutSec: number;
  buyQtyWeights: readonly [number, number, number];
  regularsBuyQtyWeights: readonly [number, number, number];
  orderRewardMultiplier: number;
  orderIntervalSec: number;
  maxOpenOrders: number;
  miniGameRepeatReward: { min: number; max: number; scoreFloor: number };
  miniGameCooldownSec: number;
  /** Bếp của bà (minigames.md §4.4, §4.11). */
  cooking: {
    /** `hold`: fill per second while held, the green zone, and where the meter stops by itself. */
    holdRatePerSec: number;
    holdZone: readonly [number, number];
    holdMax: number;
    /** `slice`: mean distance (CSS px) from the dotted line at which a cut drops to the 0.7 floor. */
    sliceTolCss: number;
    /** Score that pays the full 25 coins on a repeat play. */
    repeatScoreFull: number;
    /** One homemade product per this much play time (on top of the coin reward). */
    homemadeCooldownSec: number;
  };
  /** Studio NANO (studio-nano.md §4–§5). */
  studio: {
    maxOpenRequests: number;
    requestIntervalSec: number;
    /** Default coins of a shoot before the grade multiplier. */
    rewardCoins: number;
    gradeCoinMultiplier: { cute: number; very_cute: number; excellent: number };
    reputation: { cute: number; very_cute: number; excellent: number };
    friendship: number;
    excellentFriendshipBonus: number;
    /** Tiếng lành points for Lv1..Lv5. */
    levels: readonly number[];
  };
  mailboxCap: number;
  cozinessWeights: CozinessWeights;
  cozinessLevels: Record<RoomId, readonly number[]>;
  friendshipThresholds: { npc: readonly number[]; pet: readonly number[] };
  starThresholds: {
    npcOrders: number;
    packing: number;
    productsT2: number;
    catalogT2: number;
    photoMode: number;
    palette2: number;
    shopExpansion1: number;
    productsT3: number;
    catalogT3: number;
    chapter4: number;
    homeLayout2: number;
    chapter5: number;
  };
  stockBoxSize: number;
  unlockCosts: { shopExpansion1: number; homeLayout2: number; studio: number };
  customerWeights: { base: number; perThemeMatch: number; favorite: number };
  namedVisitorChance: number;
  maxConcurrentCustomers: number;
  customerBrowseSec: { min: number; max: number };
  orderQty: { min: number; max: number };
  orderKinds: { min: number; max: number };
  orderFavoriteWeight: number;
  orderRoundTo: number;
  friendshipPoints: {
    order: number;
    firstTalkDaily: number;
    favoriteBuy: number;
    favoriteBuyDailyCap: number;
    catPet: number;
    catPetDailyCap: number;
    catPateDaily: number;
  };
  starsPer: {
    tutorialTask: number;
    optionalTask: number;
    achievement: Record<Tier, number>;
    cozinessLevel: number;
    friendshipLevel: { npc: number; pet: number };
    miniGameLevel: number;
    setComplete: number;
    studioLevel: number;
  };
  mailboxCoins: number;
  optionalSuggestionsVisible: number;
  repeatableSuggestionsVisible: number;
  repeatableClaimCooldownSec: number;
  repeatableRewardCoins: { min: number; max: number };
  nearMaxGapTiles: number;
  albumMaxPhotos: number;
  undoMaxDepth: number;
  save: {
    debounceMs: number;
    criticalMs: number;
    backupEveryRevisions: number;
    backupMinIntervalMs: number;
    backupKeep: number;
    txRingSize: number;
  };
}

export const BALANCE: Balance = {
  startCoins: 250,
  customerIntervalSec: 30,
  customerIntervalJitterSec: 8,
  customerIntervalStepPerCozyLevelSec: 2,
  customerIntervalMinSec: 20,
  fasterCustomersCutSec: 4,
  buyQtyWeights: [50, 35, 15],
  regularsBuyQtyWeights: [35, 40, 25],
  orderRewardMultiplier: 1.5,
  orderIntervalSec: 240,
  maxOpenOrders: 3,
  miniGameRepeatReward: { min: 15, max: 25, scoreFloor: 0.7 },
  miniGameCooldownSec: 60,
  cooking: {
    holdRatePerSec: 0.45,
    holdZone: [0.78, 0.94],
    holdMax: 1.1,
    sliceTolCss: 36,
    repeatScoreFull: 0.95,
    homemadeCooldownSec: 600,
  },
  studio: {
    maxOpenRequests: 2,
    requestIntervalSec: 240,
    rewardCoins: 60,
    gradeCoinMultiplier: { cute: 1, very_cute: 1.5, excellent: 2 },
    reputation: { cute: 1, very_cute: 2, excellent: 3 },
    friendship: 10,
    excellentFriendshipBonus: 5,
    levels: [3, 8, 15, 24, 36],
  },
  mailboxCap: 7,
  cozinessWeights: {
    furniture: 5,
    decor: 3,
    fixture: 0,
    product: 1,
    productCap: 30,
    rightSpot: 4,
    themeDiversity: 2,
    themeDiversityCap: 14,
    setBonus: 15,
    maxCountedCopiesPerItem: 3,
  },
  // The studio has no Coziness levels: its own progression is Tiếng lành (balance.studio.levels).
  cozinessLevels: { home: [60, 150, 240, 300, 370], minimart: [15, 40, 75, 120, 180], studio: [] },
  friendshipThresholds: { npc: [50, 120, 240], pet: [20, 50] },
  starThresholds: {
    npcOrders: 18,
    packing: 22,
    productsT2: 22,
    catalogT2: 28,
    photoMode: 30,
    palette2: 35,
    shopExpansion1: 35,
    productsT3: 40,
    catalogT3: 55,
    chapter4: 55,
    homeLayout2: 55,
    chapter5: 80,
  },
  stockBoxSize: 4,
  unlockCosts: { shopExpansion1: 300, homeLayout2: 400, studio: 500 },
  customerWeights: { base: 1, perThemeMatch: 1, favorite: 2 },
  namedVisitorChance: 0.25,
  maxConcurrentCustomers: 2,
  customerBrowseSec: { min: 4, max: 8 },
  orderQty: { min: 2, max: 4 },
  orderKinds: { min: 1, max: 2 },
  orderFavoriteWeight: 3,
  orderRoundTo: 5,
  friendshipPoints: {
    order: 10,
    firstTalkDaily: 3,
    favoriteBuy: 1,
    favoriteBuyDailyCap: 5,
    catPet: 1,
    catPetDailyCap: 5,
    catPateDaily: 2,
  },
  starsPer: {
    tutorialTask: 1,
    optionalTask: 1,
    achievement: { bronze: 2, silver: 3, gold: 5 },
    cozinessLevel: 2,
    friendshipLevel: { npc: 2, pet: 1 },
    miniGameLevel: 1,
    setComplete: 5,
    studioLevel: 2,
  },
  mailboxCoins: 15,
  optionalSuggestionsVisible: 3,
  repeatableSuggestionsVisible: 3,
  repeatableClaimCooldownSec: 90,
  repeatableRewardCoins: { min: 10, max: 20 },
  nearMaxGapTiles: 1,
  albumMaxPhotos: 30,
  undoMaxDepth: 50,
  save: {
    debounceMs: 1500,
    criticalMs: 300,
    backupEveryRevisions: 20,
    backupMinIntervalMs: 300_000,
    backupKeep: 10,
    txRingSize: 256,
  },
};

/** Sanity checks that protect the player (softlock, ordering). Returned as messages for the validator. */
export function checkBalance(b: Balance): string[] {
  const issues: string[] = [];
  const ascending = (a: readonly number[]) => a.every((v, i) => i === 0 || v > (a[i - 1] ?? -Infinity));
  if (b.customerIntervalMinSec > b.customerIntervalSec)
    issues.push('customerIntervalMinSec > customerIntervalSec');
  if (b.miniGameRepeatReward.min < 15) issues.push('softlock: mini game repeat reward < 15 (economy §11)');
  if (!ascending(b.cozinessLevels.home) || !ascending(b.cozinessLevels.minimart)) {
    issues.push('cozinessLevels must be strictly ascending');
  }
  if (!ascending(b.studio.levels)) issues.push('studio.levels must be strictly ascending');
  if (!ascending(b.friendshipThresholds.npc) || !ascending(b.friendshipThresholds.pet)) {
    issues.push('friendshipThresholds must be strictly ascending');
  }
  if (b.startCoins < 0 || !Number.isInteger(b.startCoins))
    issues.push('startCoins must be a non-negative integer');
  return issues;
}
